import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckIcon, XMarkIcon, SparklesIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    if (user) {
      fetchSubscriptionData();
      
      // Check for payment success/cancel in URL
      const params = new URLSearchParams(window.location.search);
      if (params.get('success') === 'true') {
        alert('🎉 Payment successful! Your subscription has been upgraded.');
        // Clean URL
        window.history.replaceState({}, '', '/pricing');
      } else if (params.get('canceled') === 'true') {
        alert('Payment was canceled. You can try again anytime.');
        // Clean URL
        window.history.replaceState({}, '', '/pricing');
      }
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    try {
      // Fetch actual data from your APIs
      const [recipientsRes, messagesRes] = await Promise.all([
        axios.get(`${API_URL}/recipients/user/${user.id}`).catch(() => ({ data: { total: 0 } })),
        axios.get(`${API_URL}/recordings/user/${user.id}`).catch(() => ({ data: { total: 0 } }))
      ]);
      
      const recipientCount = recipientsRes.data.total || 0;
      const messageCount = messagesRes.data.total || 0;
      
      // Try to get subscription data
      try {
        const [subRes, usageRes] = await Promise.all([
          axios.get(`${API_URL}/subscriptions/${user.id}`),
          axios.get(`${API_URL}/subscriptions/${user.id}/usage`)
        ]);
        
        setCurrentSubscription(subRes.data.subscription);
        
        // Override usage data with actual counts from database
        const usageData = usageRes.data;
        usageData.usage.recipients.current = recipientCount;
        usageData.usage.messages.current = messageCount;
        setUsage(usageData);
      } catch (subError) {
        // Subscription doesn't exist yet - create default Free tier view
        console.log('Subscription API not available, using fallback Free tier');
        setCurrentSubscription({ tier: 'free', status: 'active' });
        
        setUsage({
          tier: 'free',
          status: 'active',
          period_start: new Date(),
          period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          usage: {
            messages: { 
              current: messageCount, 
              limit: 2, 
              remaining: Math.max(0, 2 - messageCount),
              percentage: messageCount > 0 ? Math.round((messageCount / 2) * 100) : 0
            },
            recipients: { 
              current: recipientCount, 
              limit: 2, 
              remaining: Math.max(0, 2 - recipientCount),
              percentage: recipientCount > 0 ? Math.round((recipientCount / 2) * 100) : 0
            },
            whatsapp: { 
              current: 0, 
              limit: 2, 
              remaining: 2,
              percentage: 0
            },
            podcast_sessions: { 
              current: 0, 
              limit: 0, 
              remaining: 0,
              percentage: 0
            }
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (tier) => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Validate upgrade path
    const currentTier = currentSubscription?.tier || 'free';
    const tierHierarchy = { free: 0, starter: 1, premium: 2, lifetime: 3 };
    
    if (tierHierarchy[tier] <= tierHierarchy[currentTier]) {
      alert(`You are already on the ${currentTier} plan or higher.`);
      return;
    }

    setUpgrading(true);
    setSelectedTier(tier);

    try {
      // Create Stripe Checkout session
      const response = await axios.post(`${API_URL}/payments/create-checkout-session`, {
        user_id: user.id,
        user_email: user.email,
        tier: tier,
        success_url: `${window.location.origin}/pricing?success=true`,
        cancel_url: `${window.location.origin}/pricing?canceled=true`
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create checkout session. Please try again.';
      alert(`❌ ${errorMessage}`);
      setUpgrading(false);
      setSelectedTier(null);
    }
  };

  const tiers = [
    {
      name: 'Free',
      id: 'free',
      price: '$0',
      period: 'forever',
      description: 'Experience the magic',
      features: [
        { text: '2 voice messages per month', included: true },
        { text: '2 recipients', included: true },
        { text: 'WhatsApp delivery', included: true },
        { text: 'Web portal access', included: true },
        { text: 'Voice cloning', included: false },
        { text: 'Podcast interviews', included: false },
        { text: 'Custom scheduling', included: false }
      ],
      cta: 'Current Plan',
      highlight: false
    },
    {
      name: 'Starter',
      id: 'starter',
      price: '$14.99',
      period: 'per month',
      description: 'Your voice, preserved forever',
      features: [
        { text: '15 voice messages per month', included: true },
        { text: '5 recipients', included: true },
        { text: 'Full voice cloning', included: true },
        { text: '1 podcast session per month', included: true },
        { text: 'WhatsApp delivery', included: true },
        { text: 'Custom scheduling', included: true },
        { text: 'AI chatbot', included: false }
      ],
      cta: 'Upgrade to Starter',
      highlight: false
    },
    {
      name: 'Premium',
      id: 'premium',
      price: '$39.99',
      period: 'per month',
      description: 'Unlimited legacy creation',
      features: [
        { text: 'UNLIMITED messages', included: true, bold: true },
        { text: 'UNLIMITED recipients', included: true, bold: true },
        { text: 'UNLIMITED podcast sessions', included: true, bold: true },
        { text: 'AI chatbot conversations', included: true },
        { text: 'Priority voice quality', included: true },
        { text: 'Analytics dashboard', included: true },
        { text: 'Priority support', included: true }
      ],
      cta: 'Upgrade to Premium',
      highlight: true,
      badge: 'Most Popular'
    },
    {
      name: 'Lifetime',
      id: 'lifetime',
      price: '$799',
      period: 'one-time',
      description: '50 years of guaranteed storage',
      features: [
        { text: 'All Premium features', included: true, bold: true },
        { text: 'Never pay again', included: true, bold: true },
        { text: 'Guaranteed 50-year storage', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Early access to features', included: true },
        { text: 'Legacy protection', included: true },
        { text: 'Estate planning support', included: true }
      ],
      cta: 'Get Lifetime Access',
      highlight: false,
      badge: 'Best Value'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-4">
          <span className="text-blue-800 font-semibold text-sm">🎖️ Special Military Pricing Available</span>
        </div>
        <h1 className="text-5xl font-extrabold text-gray-900 mb-4">
          Stay Connected During Deployment
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          One low price for unlimited messages. Send bedtime stories, birthday wishes, and daily encouragement to your family.
        </p>
      </div>

      {/* Current Subscription Status */}
      {!loading && currentSubscription && usage && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-12 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <SparklesIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">
                  Current Plan: <span className="text-blue-600 capitalize">{currentSubscription.tier}</span>
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                Status: <span className="font-medium capitalize text-green-600">{currentSubscription.status}</span>
              </p>
              {currentSubscription.current_period_end && currentSubscription.tier !== 'lifetime' && (
                <p className="text-xs text-gray-500 mt-1">
                  Renews: {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                </p>
              )}
            </div>
            {currentSubscription.tier === 'free' && (
              <button
                onClick={() => document.getElementById('pricing-cards').scrollIntoView({ behavior: 'smooth' })}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:shadow-xl transition-all flex items-center space-x-2"
              >
                <span>Upgrade Now</span>
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Usage Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Messages</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {usage.usage.messages.current}
                <span className="text-base font-normal text-gray-500">/{usage.usage.messages.limit === -1 ? '∞' : usage.usage.messages.limit}</span>
              </p>
              {usage.usage.messages.limit !== -1 && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      usage.usage.messages.percentage >= 100
                        ? 'bg-red-500'
                        : usage.usage.messages.percentage >= 80
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, usage.usage.messages.percentage)}%` }}
                  />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">This month</p>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recipients</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {usage.usage.recipients.current}
                <span className="text-base font-normal text-gray-500">/{usage.usage.recipients.limit === -1 ? '∞' : usage.usage.recipients.limit}</span>
              </p>
              {usage.usage.recipients.limit !== -1 && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      usage.usage.recipients.percentage >= 100
                        ? 'bg-red-500'
                        : usage.usage.recipients.percentage >= 80
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, usage.usage.recipients.percentage)}%` }}
                  />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">Total added</p>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">WhatsApp</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {usage.usage.whatsapp.current}
                <span className="text-base font-normal text-gray-500">/{usage.usage.whatsapp.limit === -1 ? '∞' : usage.usage.whatsapp.limit}</span>
              </p>
              {usage.usage.whatsapp.limit !== -1 && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      usage.usage.whatsapp.percentage >= 100
                        ? 'bg-red-500'
                        : usage.usage.whatsapp.percentage >= 80
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, usage.usage.whatsapp.percentage)}%` }}
                  />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">This month</p>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Podcasts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {usage.usage.podcast_sessions.current}
                <span className="text-base font-normal text-gray-500">/{usage.usage.podcast_sessions.limit === -1 ? '∞' : usage.usage.podcast_sessions.limit}</span>
              </p>
              {usage.usage.podcast_sessions.limit !== -1 && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      usage.usage.podcast_sessions.percentage >= 100
                        ? 'bg-red-500'
                        : usage.usage.podcast_sessions.percentage >= 80
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, usage.usage.podcast_sessions.percentage)}%` }}
                  />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">This month</p>
            </div>
          </div>
          
          {/* Warning if approaching limits */}
          {(usage.usage.messages.percentage >= 80 || usage.usage.recipients.percentage >= 80 || usage.usage.whatsapp.percentage >= 80) && currentSubscription.tier !== 'premium' && currentSubscription.tier !== 'lifetime' && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-medium">
                ⚠️ You're approaching your plan limits. Consider upgrading to avoid interruptions.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pricing Cards */}
      <div id="pricing-cards" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
        {tiers.map((tier) => {
          const isCurrentTier = currentSubscription?.tier === tier.id;
          const canUpgrade = !isCurrentTier && user;
          
          return (
            <div
              key={tier.id}
              className={`relative rounded-2xl border-2 ${
                tier.highlight
                  ? 'border-purple-500 shadow-2xl scale-105'
                  : 'border-gray-200 shadow-lg'
              } bg-white overflow-hidden transition-all hover:shadow-xl`}
            >
              {tier.badge && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                  {tier.badge}
                </div>
              )}
              
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
                
                <div className="mb-6">
                  <span className="text-4xl font-extrabold text-gray-900">{tier.price}</span>
                  <span className="text-gray-600 ml-2">/{tier.period}</span>
                </div>

                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      {feature.included ? (
                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XMarkIcon className="h-5 w-5 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${feature.bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => canUpgrade && handleUpgrade(tier.id)}
                  disabled={isCurrentTier || !user || (upgrading && selectedTier === tier.id)}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center space-x-2 ${
                    isCurrentTier
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : tier.highlight
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:scale-105'
                      : 'bg-gray-900 text-white hover:bg-gray-800 transform hover:scale-105'
                  } ${
                    upgrading && selectedTier === tier.id ? 'opacity-75 cursor-wait' : ''
                  }`}
                >
                  {upgrading && selectedTier === tier.id ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Upgrading...</span>
                    </>
                  ) : (
                    <span>{isCurrentTier ? 'Current Plan' : tier.cta}</span>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 rounded-2xl p-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          Frequently Asked Questions
        </h2>
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Can I upgrade anytime?</h3>
            <p className="text-gray-600 text-sm">
              Yes! Upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">What happens to my data if I cancel?</h3>
            <p className="text-gray-600 text-sm">
              Your voice and messages are preserved. You can reactivate anytime. Lifetime plans never expire.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Is voice cloning included?</h3>
            <p className="text-gray-600 text-sm">
              Voice cloning is included in Starter, Premium, and Lifetime plans. Free tier uses pre-built voices.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">How does WhatsApp delivery work?</h3>
            <p className="text-gray-600 text-sm">
              Messages are sent via WhatsApp Business API. Recipients receive audio messages in your voice automatically.
            </p>
          </div>
        </div>
      </div>

      {/* CTA for non-logged in users */}
      {!user && (
        <div className="mt-12 text-center bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-12 border-2 border-purple-200">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Ready to preserve your voice?</h3>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Start with our free plan today. No credit card required.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <span>Get Started Free</span>
            <ArrowRightIcon className="h-5 w-5" />
          </button>
          <p className="text-sm text-gray-500 mt-4">
            ✓ No credit card required • ✓ 2 free messages • ✓ Cancel anytime
          </p>
        </div>
      )}
      
      {/* Trust Indicators */}
      <div className="mt-16 text-center">
        <p className="text-sm text-gray-500 mb-4">Trusted by families worldwide</p>
        <div className="flex justify-center items-center space-x-8 text-gray-400">
          <div className="flex items-center space-x-2">
            <CheckIcon className="h-5 w-5 text-green-500" />
            <span className="text-sm">Secure & Private</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckIcon className="h-5 w-5 text-green-500" />
            <span className="text-sm">Cancel Anytime</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckIcon className="h-5 w-5 text-green-500" />
            <span className="text-sm">24/7 Support</span>
          </div>
        </div>
      </div>
    </div>
  );
}
